import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { trpc } from "~/lib/trpc";
import { VariantInsights } from "~/routes/(app)/pages/$pageId/-components/variant-insights";
import { EditElementButton } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/edit-element-button";
import { OpenStudioButton } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/open-studio-button";
import { stripHtml } from "~/utils/strip-html";

export const DEFAULT_INSIGHTS_TIME_PERIOD = 7;

export const TIME_PERIODS = [
  { label: "Last 24 hours", value: 1 },
  { label: "Last 3 days", value: 3 },
  { label: "Last 7 days", value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
] as const;

export const Route = createFileRoute(
  "/(app)/pages/$pageId/variants/$variantId/",
)({
  component: RouteComponent,
  validateSearch: (search): { timePeriod?: number } => {
    const timePeriod = TIME_PERIODS.find(
      (period) => period.value === search.timePeriod,
    )?.value;

    return { timePeriod };
  },
  loaderDeps: ({ search: { timePeriod } }) => ({
    timePeriod,
  }),
  loader: async ({
    deps: { timePeriod },
    context: { queryClient },
    params,
  }) => {
    const [{ elements }, { insights }] = await Promise.all([
      queryClient.ensureQueryData(
        trpc.variants.getVariantPersonalisedElements.queryOptions({
          variantId: params.variantId,
          pageId: params.pageId,
        }),
      ),
      queryClient.ensureQueryData(
        trpc.variants.getVariantInsights.queryOptions({
          variantId: params.variantId,
          pageId: params.pageId,
          timePeriod: timePeriod ?? DEFAULT_INSIGHTS_TIME_PERIOD,
        }),
      ),
    ]);

    return { elements, insights };
  },
});

function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();

  const {
    data: { variant },
  } = useSuspenseQuery(
    trpc.variants.getVariant.queryOptions({
      variantId: params.variantId,
      pageId: params.pageId,
    }),
  );

  const {
    data: { elements },
  } = useSuspenseQuery(
    trpc.variants.getVariantPersonalisedElements.queryOptions({
      variantId: params.variantId,
      pageId: params.pageId,
    }),
  );

  return (
    <div className="flex flex-col gap-5 w-full">
      <VariantInsights
        pageId={params.pageId}
        variantId={params.variantId}
        timePeriod={search.timePeriod ?? DEFAULT_INSIGHTS_TIME_PERIOD}
      />

      <div className="border rounded-lg px-5 pt-4.5 pb-2.5">
        <h2 className="text-lg font-medium mb-1.5">Personalised elements</h2>

        {elements.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 border-2 border-dashed rounded-md p-10 my-2">
            <p className="text-sm text-gray-500">
              This variant is not personalised yet
            </p>
            <OpenStudioButton studioUrl={variant.studioUrl} />
          </div>
        )}

        {elements.length > 0 && (
          <>
            <div className="flex items-center gap-6 mb-1.5">
              <div className="flex-1">
                <span className="text-xs text-gray-500">Original</span>
              </div>
              <div className="flex-1">
                <span className="text-xs text-gray-500">Personalised</span>
              </div>
            </div>

            <ul className="border-t">
              {elements.map((element, index) => (
                <li
                  key={index}
                  className="flex gap-6 py-2.5 border-b last:border-none relative group"
                >
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">
                      {element.originalContent ? (
                        <ElementText html={element.originalContent} />
                      ) : (
                        <span className="italic">Not available</span>
                      )}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      <ElementText html={element.personalisedContent} />
                    </p>
                  </div>
                  <div className="absolute right-0 top-0 bottom-0 flex items-center justify-center group-hover:opacity-100 opacity-0 transition-opacity duration-150">
                    <EditElementButton
                      studioUrl={variant.studioUrl}
                      selector={element.selector}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function ElementText({ html }: { html: string }) {
  const text = useMemo(() => stripHtml(html), [html]);
  return <p className="text-sm">{text}</p>;
}
